import { Badge, Box, Card, Group, Stack, Switch, Text } from '@mantine/core';
import { Info, Lock } from 'react-feather';
import { AudioLines } from 'lucide-react';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { coachSessionPrepApi } from '../../../../services/api/coachSessionPrep';

// MED-1099 — tokens taken directly from the Figma frame (node 1993:18215). The
// card keeps its distinct AI treatment (cool-lavender fill, no border); the
// waveform icon renders bare (no chip), and the info box is a bordered panel
// with the ⓘ on the second (caveat) line.
const CARD_BG = '#f0f5fb';
const TITLE_COLOR = '#1a1a2e';
const TEXT_NAVY = '#203358';
const AI_ICON_COLOR = '#3730a3'; // waveform accent
const INFO_BOX_BG = '#ebf2fa';
const INFO_BOX_BORDER = '#b0cce0';
const LOCKED_BADGE_BG = '#9ca3af';
const CAVEAT_COLOR = '#888888';

/**
 * MED-876 AC3 — per-group AI Listening card on the Coach Session Prep page.
 *
 * Visual reference: screenshots #1 (in context) and #5 (close-up).
 *
 * - Always absent from the parent when `modality.ai_features_enabled=false`
 *   (the parent gates rendering — this component assumes AI features are on).
 * - Before the lock window: shows the current state; the CM-only toggle is
 *   visible per AC3 ("Coach control of this toggle is out of scope — see
 *   MED-866"). V1 surfaces the toggle to any cohort coach since strict
 *   CM-role gating ships with MED-866.
 * - After lock: shows the **Locked** badge and a frozen On/Off state.
 */
export interface AIListeningCardProps {
  sessionId: string;
  enabled: boolean;
  locked: boolean;
  /** When parent has more authoritative info than `locked`, override here. */
  onToggleResult?: (next: {
    ai_listening_enabled: boolean;
    ai_listening_locked: boolean;
  }) => void;
  /**
   * MED-909 AC4 — render a static On/Off state badge instead of the toggle.
   * Peer leaders cannot change AI Listening (CM-controlled), so the peer-leader
   * prep page passes true. The toggle endpoint is never called in this mode.
   */
  readOnly?: boolean;
  /**
   * MED-909 QA — inject the toggle call. Defaults to the coach endpoint
   * (`coachSessionPrepApi.setAiListening`). The peer-leader prep page passes the
   * peer endpoint here, since the coach endpoint hard-rejects peer-led sessions.
   */
  onToggle?: (
    next: boolean
  ) => Promise<{ ai_listening_enabled: boolean; ai_listening_locked: boolean }>;
}

export function AIListeningCard({
  sessionId,
  enabled,
  locked,
  onToggleResult,
  readOnly = false,
  onToggle,
}: AIListeningCardProps) {
  const [optimistic, setOptimistic] = useState<boolean>(enabled);
  const [saving, setSaving] = useState(false);

  const handleToggle = async (next: boolean): Promise<void> => {
    if (locked || saving) return;
    setOptimistic(next);
    setSaving(true);
    try {
      const result = onToggle
        ? await onToggle(next)
        : await coachSessionPrepApi.setAiListening(sessionId, next);
      setOptimistic(result.ai_listening_enabled);
      onToggleResult?.(result);
    } catch {
      setOptimistic(!next);
      notifications.show({
        color: 'red',
        title: 'Could not update AI Listening',
        message: 'Try again in a moment.',
      });
    } finally {
      setSaving(false);
    }
  };

  const stateLabel = optimistic ? 'On' : 'Off';

  return (
    <Card padding={24} radius={16} style={{ backgroundColor: CARD_BG }}>
      <Stack gap={14}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={16} style={{ flex: 1, minWidth: 0 }}>
            <Group gap={4} align="center">
              <AudioLines size={18} color={AI_ICON_COLOR} />
              <Text fw={600} fz={15} c={TITLE_COLOR}>
                AI listening
              </Text>
            </Group>
            <Text fz={13} c={TEXT_NAVY}>
              <Text span fw={900} inherit>
                {stateLabel}
              </Text>
              {optimistic
                ? ' — participants will be notified'
                : ' — AI Listening is off for this group'}
            </Text>
          </Stack>
          {locked ? (
            <Badge
              variant="filled"
              radius="xl"
              leftSection={<Lock size={11} color="#fff" />}
              styles={{
                root: {
                  backgroundColor: LOCKED_BADGE_BG,
                  textTransform: 'none',
                  fontWeight: 600,
                },
                label: { fontSize: 11 },
              }}
            >
              Locked
            </Badge>
          ) : readOnly ? (
            <Badge
              color={optimistic ? 'indigo' : 'gray'}
              variant={optimistic ? 'filled' : 'light'}
              radius="xl"
              styles={{ root: { textTransform: 'none', fontWeight: 500 } }}
            >
              {optimistic ? 'On' : 'Off'}
            </Badge>
          ) : (
            <Switch
              size="sm"
              checked={optimistic}
              disabled={saving}
              onChange={e => void handleToggle(e.currentTarget.checked)}
              aria-label="Toggle AI Listening for this group"
            />
          )}
        </Group>

        {optimistic && (
          <Box
            style={{
              backgroundColor: INFO_BOX_BG,
              border: `1px solid ${INFO_BOX_BORDER}`,
              borderRadius: 8,
              padding: '15px 17px 17px',
            }}
          >
            <Stack gap={10}>
              <Text fz={13} c={TEXT_NAVY} style={{ lineHeight: 1.5 }}>
                Participants will be listened to during the session.
              </Text>
              <Group gap={4} align="flex-start" wrap="nowrap">
                <Info
                  size={13}
                  color={CAVEAT_COLOR}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Text fz={11} c={CAVEAT_COLOR} style={{ lineHeight: 1.5 }}>
                  You can turn this off during the session. If turned off before
                  the session starts, it cannot be re-enabled mid-session.
                </Text>
              </Group>
            </Stack>
          </Box>
        )}
      </Stack>
    </Card>
  );
}

export default AIListeningCard;
